import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const styles = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0F172A', fontFamily: 'system-ui, sans-serif',
  },
  card: {
    background: '#1E293B', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 380,
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', border: '1px solid #334155',
  },
  title: { margin: '0 0 0.5rem', fontSize: '1.4rem', fontWeight: 700, color: '#F8FAFC' },
  subtitle: { margin: '0 0 1.5rem', fontSize: '0.9rem', color: '#94A3B8' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', boxSizing: 'border-box', outline: 'none', marginBottom: '1rem',
    background: '#1E293B', color: '#F8FAFC',
  },
  btn: {
    width: '100%', padding: '0.75rem', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontSize: '1rem', fontWeight: 600,
    cursor: 'pointer', marginTop: 4,
  },
  error: { color: '#EF4444', fontSize: '0.875rem', marginBottom: '1rem' },
};

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>{t('acceptInvite.invalidTitle')}</h1>
          <p style={styles.subtitle}>{t('acceptInvite.invalidSubtitle')}</p>
          <Link to="/admin/login" style={{ color: '#7C3AED', fontWeight: 600 }}>{t('acceptInvite.goToLogin')}</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError(t('acceptInvite.minLength')); return; }
    if (password !== confirm) { setError(t('acceptInvite.mismatch')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'INVITE_NOT_FOUND') { setError(t('acceptInvite.alreadyUsed')); }
        else if (data.error === 'INVITE_EXPIRED') { setError(t('acceptInvite.expired')); }
        else { setError(data.message || t('acceptInvite.failed')); }
        return;
      }
      login(data.accessToken, data.refreshToken, true);
      try {
        const claims = JSON.parse(atob(data.accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        navigate(claims.role === 'STAFF' ? '/admin/queue' : '/admin/queue');
      } catch {
        navigate('/admin/queue');
      }
    } catch {
      setError(t('acceptInvite.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t('acceptInvite.title')}</h1>
        <p style={styles.subtitle}>{t('acceptInvite.subtitle')}</p>
        {error && <p style={styles.error}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={styles.label}>{t('acceptInvite.passwordLabel')}</label>
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <input
              style={{ ...styles.input, marginBottom: 0, paddingRight: '3rem' }}
              type={showPw ? 'text' : 'password'}
              placeholder={t('acceptInvite.passwordPlaceholder')}
              value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
            />
            <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#94A3B8', padding: 4 }}>
              {showPw ? t('acceptInvite.hide') : t('acceptInvite.show')}
            </button>
          </div>
          <label style={styles.label}>{t('acceptInvite.confirmLabel')}</label>
          <input
            style={styles.input} type="password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} required
          />
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? t('acceptInvite.submitting') : t('acceptInvite.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
